import {
  Fn,
  abs,
  atan,
  clamp,
  dot,
  float,
  fract,
  length,
  max,
  min,
  select,
  sign,
  smoothstep,
  vec2,
} from 'three/tsl';
import type { Node } from 'three/webgpu';

type F = Node<'float'>;
type V2 = Node<'vec2'>;

const TAU = Math.PI * 2;

/**
 * Signed-distance primitives for the instrument display.
 *
 * Everything is evaluated in atlas pixels so a stroke reads the same width in
 * every panel regardless of the panel's size — a property the 2D canvas this
 * replaces got for free.
 */

/** Distance from `p` to segment `a`-`b`. Round caps fall out of this for free. */
export const sdSegment = /*#__PURE__*/ Fn(([p, a, b]: [V2, V2, V2]) => {
  const pa = p.sub(a).toVar();
  const ba = b.sub(a).toVar();
  const h = clamp(dot(pa, ba).div(max(dot(ba, ba), float(1e-6))), 0, 1);
  return length(pa.sub(ba.mul(h)));
});

/** Distance from `p` to an origin-centred box with half-extents `b`. */
export const sdBox = /*#__PURE__*/ Fn(([p, b]: [V2, V2]) => {
  const d = abs(p).sub(b).toVar();
  return length(max(d, vec2(0, 0))).add(min(max(d.x, d.y), 0));
});

/** Distance from `p` to an origin-centred circle of radius `r`. */
export const sdCircle = /*#__PURE__*/ Fn(([p, r]: [V2, F]) => length(p).sub(r));

/**
 * Distance to a progress arc of radius `r` covering `turns` of a full circle.
 *
 * The sweep starts at 3 o'clock and runs clockwise on screen, matching
 * `CanvasRenderingContext2D.arc` in a y-down space. Outside the sweep the
 * distance falls back to the nearer end cap, which gives the round caps the
 * 2D context drew.
 */
export const sdArc = /*#__PURE__*/ Fn(([p, r, turns]: [V2, F, F]) => {
  const t = clamp(turns, 0, 1).toVar();
  const sweep = t.mul(TAU).toVar();

  // atan returns -PI..PI; shift into 0..TAU so the comparison is a single test.
  const angle = fract(atan(p.y, p.x).div(TAU).add(1)).mul(TAU).toVar();

  const onRing = abs(length(p).sub(r));
  const startCap = length(p.sub(vec2(r, 0)));
  const endCap = length(p.sub(vec2(sweep.cos().mul(r), sweep.sin().mul(r))));

  return select(angle.lessThanEqual(sweep), onRing, min(startCap, endCap));
});

/** Antialiased coverage of a stroke of total width `width` around a distance field. */
export const strokeMask = /*#__PURE__*/ Fn(([distance, width]: [F, F]) => {
  const half = width.mul(0.5);
  return smoothstep(half.add(1), half.sub(1), distance);
});

/** Antialiased coverage of the interior of a distance field. */
export const fillMask = /*#__PURE__*/ Fn(([distance]: [F]) => smoothstep(1, -1, distance));

/** Dash gate for a stroke, given the distance travelled along the path in pixels. */
export const dashMask = /*#__PURE__*/ Fn(([phase, on, off]: [F, F, F]) => {
  const period = on.add(off);
  const local = fract(phase.div(period)).mul(period);
  return smoothstep(on.add(1), on.sub(1), local);
});

/** Hard clip used by the fuzz waveform ramp. */
export const squareOff = /*#__PURE__*/ Fn(([value, threshold, amount]: [F, F, F]) =>
  select(abs(value).lessThan(threshold), float(0), sign(value).mul(amount)),
);
